"use client";

import { Button } from "@/components/ui/button";
import { TooltipContent } from "@/components/ui/tooltip-content";
import { useUser } from "@/lib/auth/session";
import { addBookmark, deleteBookmark, getBookmark } from "@/lib/blog/bookmarks";
import { cn } from "@/lib/utils";
import { Bookmark, BookmarkCheck, UserIcon } from "lucide-react";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Spinner } from "../ui/spinner";

interface BookmarkButtonProps {
	postId: string;
	expanded?: boolean;
	dark?: boolean;
}

export default function BookmarkButton({
	postId,
	expanded = false,
	dark = false,
}: BookmarkButtonProps) {
	const { user, isUserLoading, error } = useUser();

	const [isBookmarked, setIsBookmarked] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	const fetchIsBookmarked = useCallback(async () => {
		// Skip if no user is logged in
		if (!user?.id) {
			setIsBookmarked(false);
			setIsLoading(false);
			return;
		}

		const isMounted = true;
		setIsLoading(true);

		try {
			const { data, error } = await getBookmark(user.id, postId);

			if (!isMounted) return;

			if (error) {
				throw error;
			}

			setIsBookmarked(!!data?.length);
		} catch (err) {
			console.error("Error checking bookmark status:", err);
			toast.error("Failed to check bookmark status");
			// Consider setting an error state if you want to show an error UI
		} finally {
			if (isMounted) {
				setIsLoading(false);
			}
		}
	}, [user?.id, postId]);

	// Sync bookmark state when user or postId changes
	useEffect(() => {
		let isMounted = true;

		const fetchData = async () => {
			await fetchIsBookmarked();
		};

		if (user?.id) {
			fetchData();
		} else {
			// Reset state if user logs out
			if (isMounted) {
				setIsBookmarked(false);
				setIsLoading(false);
			}
		}

		return () => {
			isMounted = false;
		};
	}, [user?.id, fetchIsBookmarked]);

	const handleBookmarkClick = async () => {
		if (!user) {
			toast("User not logged in.. Please login to bookmark.", {
				icon: <UserIcon className="w-4 h-4" />,
				style: {
					background: "yellow",
					color: "black",
				},
			});
			return;
		}

		if (isBookmarked) {
			const { error } = await deleteBookmark(user?.id, postId);
			if (error) {
				console.error(error);
				return;
			}
		} else {
			const { error } = await addBookmark(user?.id, postId);
			if (error) {
				console.error(error);
				return;
			}
		}
		setIsBookmarked(!isBookmarked);
	};

	if (isUserLoading || isLoading) {
		return <Spinner className="size-3 self-center" />;
	}
	if (error) {
		console.error(error);
	}

	if (!user) {
		return (
			<TooltipContent content={isBookmarked ? "Bookmarked" : "Bookmark"}>
				<Link
					href="/auth/sign-in"
					className={cn(
						"flex gap-1 items-center rounded-xl px-1 h-6 bg-transparent hover:bg-primary/20 shadow-none",
						"transition-all hover:bg-yellow-300 text-primary hover:text-black hover:translate-y-[-2px]",
						isBookmarked ? "bg-background/40" : "",
						dark ? "text-white" : "text-primary",
					)}
				>
					<Bookmark className={cn("size-4", expanded ? "size-5" : "")} />
				</Link>
			</TooltipContent>
		);
	}

	return (
		<TooltipContent content={isBookmarked ? "Bookmarked" : "Bookmark"}>
			<Button
				type="button"
				onClick={handleBookmarkClick}
				className={cn(
					"flex gap-1 items-center rounded-xl px-1 h-6 bg-transparent hover:bg-primary/20 shadow-none",
					"transition-all hover:bg-yellow-300 text-primary hover:text-black hover:translate-y-[-2px]",
					isBookmarked ? "bg-background/40" : "",
					dark ? "text-white" : "text-primary",
				)}
			>
				{isBookmarked ? (
					<BookmarkCheck className={cn("size-4", expanded ? "size-5" : "")} />
				) : (
					<Bookmark className={cn("size-4", expanded ? "size-5" : "")} />
				)}
				{expanded && (
					<span className={cn("text-xs font-bold", expanded ? "text-sm" : "")}>
						{isBookmarked ? "Bookmarked" : "Bookmark"}
					</span>
				)}
			</Button>
		</TooltipContent>
	);
}
